// src/cockpit4/components/WorkflowTemplateLibrary.tsx
// ============================================
// 工作流中心 · P0-功能1：预置工作流模板库
//
// 功能：
//   1. 模板分类展示（按类别分组，Tab 切换：全部 / 营销 / 销售 / 运营 / 人事 / 财务）
//   2. 模板搜索（关键词匹配名称 / 描述 / 预期效果）
//   3. 模板预览（点击展开 DAG 节点列表 + 说明文档 + 默认参数 + 预期效果）
//   4. 一键套用（调用 RuntimeManager.query('workflow', { type: 'instantiate_template', templateId })
//      由 WorkflowRuntime 创建工作流实例）
//   5. 模板版本管理（展示版本号 + 创建时间）
//   6. 自定义模板保存（用户可将自有工作流保存为模板，持久化到 localStorage）
//
// 数据来源：
//   - 预置模板：BUILTIN_TEMPLATES 静态配置（合法，模板本身是预设的，非"假数据"）
//   - 自定义模板：localStorage（key: xeaos_workflow_templates），无自定义模板时只展示预置模板
//   - 一键套用：RuntimeManager → WorkflowRuntime（query type: instantiate_template）
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutTemplate,
  Search,
  RefreshCw,
  Loader2,
  Sparkles,
  X,
  CheckCircle2,
  XCircle,
  Plus,
  Trash2,
  Save,
  Filter,
  GitBranch,
  Bot,
  Database,
  Repeat,
  ShieldCheck,
  Bell,
  Clock,
  Globe,
  Code,
  Megaphone,
  Handshake,
  Activity,
  Users,
  Wallet,
  FileText,
  TrendingUp,
  AlertTriangle,
  Layers,
  Calendar,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

// ============================================
// 类型定义
// ============================================

/** 模板类别 */
type TemplateCategory = "marketing" | "sales" | "operations" | "hr" | "finance";

/** DAG 节点类型 */
type TemplateNodeType =
  | "ai_call"
  | "data_process"
  | "condition"
  | "loop"
  | "approval"
  | "notification"
  | "wait"
  | "http"
  | "script";

/** DAG 节点 */
interface TemplateNode {
  id: string;
  type: TemplateNodeType;
  label: string;
  config?: Record<string, any>;
}

/** 工作流模板 */
interface WorkflowTemplate {
  id: string;
  name: string;
  category: TemplateCategory;
  description: string;
  expectedEffect: string; // 预期效果
  nodes: TemplateNode[]; // DAG 节点
  edges: { from: string; to: string }[];
  defaultParams: Record<string, string>;
  version: string;
  createdAt: string;
  /** 来源标记：builtin 预置 / custom 用户保存 */
  source?: "builtin" | "custom";
}

// ============================================
// 常量
// ============================================

/** 自定义模板 localStorage key */
const CUSTOM_TEMPLATES_STORAGE_KEY = "xeaos_workflow_templates";

/** 类别元信息（标签 / 颜色 / 图标） */
const CATEGORY_META: Record<
  TemplateCategory,
  { label: string; color: string; icon: typeof Megaphone }
> = {
  marketing: { label: "营销", color: "#F472B6", icon: Megaphone },
  sales: { label: "销售", color: "#34D399", icon: Handshake },
  operations: { label: "运营", color: "#38BDF8", icon: Activity },
  hr: { label: "人事", color: "#FBBF24", icon: Users },
  finance: { label: "财务", color: "#A78BFA", icon: Wallet },
};

/** 类别筛选选项（含"全部"） */
const CATEGORY_FILTERS: { id: "all" | TemplateCategory; label: string }[] = [
  { id: "all", label: "全部" },
  { id: "marketing", label: "营销" },
  { id: "sales", label: "销售" },
  { id: "operations", label: "运营" },
  { id: "hr", label: "人事" },
  { id: "finance", label: "财务" },
];

/** 节点类型元信息（标签 / 颜色 / 图标） */
const NODE_TYPE_META: Record<
  TemplateNodeType,
  { label: string; color: string; icon: typeof Bot }
> = {
  ai_call: { label: "AI 调用", color: "#A78BFA", icon: Bot },
  data_process: { label: "数据处理", color: "#38BDF8", icon: Database },
  condition: { label: "条件分支", color: "#FBBF24", icon: GitBranch },
  loop: { label: "循环", color: "#34D399", icon: Repeat },
  approval: { label: "审批", color: "#F87171", icon: ShieldCheck },
  notification: { label: "通知", color: "#22D3EE", icon: Bell },
  wait: { label: "等待", color: "#9CA3AF", icon: Clock },
  http: { label: "HTTP 请求", color: "#60A5FA", icon: Globe },
  script: { label: "脚本", color: "#F472B6", icon: Code },
};

/** 节点类型下拉选项 */
const NODE_TYPE_OPTIONS: TemplateNodeType[] = [
  "ai_call",
  "data_process",
  "condition",
  "loop",
  "approval",
  "notification",
  "wait",
  "http",
  "script",
];

// ============================================
// 预置模板（20 个：5 类 × 4 个）
// 每个模板含 3-6 个节点的 DAG + 默认参数 + 说明 + 预期效果
// ============================================

const BUILTIN_TEMPLATES: WorkflowTemplate[] = [
  // ---------- 营销类 ----------
  {
    id: "mkt_content_production",
    name: "内容生产流程",
    category: "marketing",
    description: "从选题到发布的内容生产全流程，含 AI 初稿、人工审核、多平台分发。",
    expectedEffect: "内容产出效率提升 3 倍，审核通过率 ≥ 85%，多平台同步发布。",
    nodes: [
      { id: "n1", type: "ai_call", label: "选题生成", config: { model: "glm-5" } },
      { id: "n2", type: "ai_call", label: "AI 初稿撰写", config: { model: "glm-5" } },
      { id: "n3", type: "approval", label: "人工审核" },
      { id: "n4", type: "data_process", label: "多平台适配" },
      { id: "n5", type: "notification", label: "发布通知" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
    ],
    defaultParams: {
      content_type: "article",
      target_platforms: "wechat,douyin,xhs",
      tone: "professional",
    },
    version: "1.2.0",
    createdAt: "2025-03-12T09:00:00Z",
    source: "builtin",
  },
  {
    id: "mkt_campaign_planning",
    name: "活动策划流程",
    category: "marketing",
    description: "营销活动从目标设定到上线的完整策划流程，含预算审批与物料制作。",
    expectedEffect: "活动上线周期缩短 40%，预算超支率 < 5%。",
    nodes: [
      { id: "n1", type: "data_process", label: "目标与受众设定" },
      { id: "n2", type: "approval", label: "预算审批" },
      { id: "n3", type: "ai_call", label: "活动方案生成" },
      { id: "n4", type: "ai_call", label: "物料制作" },
      { id: "n5", type: "notification", label: "上线通知" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
    ],
    defaultParams: {
      campaign_type: "brand",
      budget_ceiling: "50000",
      duration_days: "7",
    },
    version: "1.0.3",
    createdAt: "2025-04-01T10:30:00Z",
    source: "builtin",
  },
  {
    id: "mkt_ad_optimization",
    name: "投放优化流程",
    category: "marketing",
    description: "广告投放数据采集、效果分析、策略调整与预算重分配的闭环优化流程。",
    expectedEffect: "ROAS 提升 ≥ 20%，单次转化成本下降 15%。",
    nodes: [
      { id: "n1", type: "http", label: "投放数据采集", config: { source: "ad_platform" } },
      { id: "n2", type: "data_process", label: "效果归因分析" },
      { id: "n3", type: "ai_call", label: "策略优化建议" },
      { id: "n4", type: "condition", label: "是否调整预算" },
      { id: "n5", type: "data_process", label: "预算重分配" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
    ],
    defaultParams: {
      data_window_days: "7",
      optimization_goal: "roas",
      min_budget: "1000",
    },
    version: "2.0.0",
    createdAt: "2025-05-18T14:00:00Z",
    source: "builtin",
  },
  {
    id: "mkt_hotspot_response",
    name: "热点响应流程",
    category: "marketing",
    description: "热点事件监测、相关性判断、快速内容生成与审核发布的应急响应流程。",
    expectedEffect: "热点响应时长 < 2 小时，借势内容产出 ≥ 1 篇/热点。",
    nodes: [
      { id: "n1", type: "http", label: "热点监测" },
      { id: "n2", type: "ai_call", label: "相关性判断" },
      { id: "n3", type: "condition", label: "是否借势" },
      { id: "n4", type: "ai_call", label: "快速内容生成" },
      { id: "n5", type: "approval", label: "加急审核" },
      { id: "n6", type: "notification", label: "发布" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      monitor_keywords: "行业,竞品,热点",
      response_sla_hours: "2",
      channels: "wechat,weibo",
    },
    version: "1.1.0",
    createdAt: "2025-06-05T08:15:00Z",
    source: "builtin",
  },
  // P12 短板7：事件驱动引擎预置模板（由 EventBus 触发，全部打通 Token 节制）
  {
    id: "mkt_auto_topic_pool",
    name: "自动选题计划",
    category: "marketing",
    description: "事件驱动订阅（0点/每周）→ 多渠道采集热点 → AI 生成选题池 → 去重 + 品牌/平台映射排序 → 落盘 Vault 选题池。",
    expectedEffect: "选题命中率提升，多平台内容不再一鱼多吃；选题池自动落盘供内容工厂取用。",
    nodes: [
      { id: "n1", type: "http", label: "多渠道热点采集", config: { source: "hotspot_engines" } },
      { id: "n2", type: "ai_call", label: "智能选题生成", config: { engine: "TopicEngine" } },
      { id: "n3", type: "data_process", label: "去重与平台映射" },
      { id: "n4", type: "condition", label: "命中品牌/平台策略" },
      { id: "n5", type: "data_process", label: "选题池落盘 Vault" },
      { id: "n6", type: "notification", label: "推送选题计划" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      platforms: "wechat,douyin,xhs,zhihu",
      hot_window_days: "7",
      token_budget_yuan: "2",
      dedup_window: "30",
    },
    version: "1.0.0",
    createdAt: "2026-09-04T08:00:00Z",
    source: "builtin",
  },
  {
    id: "ops_ceo_morning_brief",
    name: "CEO 晨报生成",
    category: "operations",
    description: "事件驱动订阅（工作日 9:00）→ 检索昨日 KPI/运营数据 → AI 生成晨报 → Token 预算不足自动降级规则化 → 落盘 Vault 并推送 CEO。",
    expectedEffect: "每日晨报准时产出，Token 预算可控（默认 ¥1/日），关键数据零遗漏。",
    nodes: [
      { id: "n1", type: "http", label: "昨日 KPI/运营数据拉取" },
      { id: "n2", type: "ai_call", label: "晨报内容生成", config: { engine: "Hermes", guarded: "token" } },
      { id: "n3", type: "condition", label: "Token 预算是否充足" },
      { id: "n4", type: "data_process", label: "保存晨报到 Vault" },
      { id: "n5", type: "notification", label: "推送 CEO 与关键人" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
    ],
    defaultParams: {
      date: "",
      token_budget_yuan: "1",
      notify_channels: "wechat_work",
      include_risks: "true",
    },
    version: "1.0.0",
    createdAt: "2026-09-04T08:00:00Z",
    source: "builtin",
  },

  // ---------- 销售类 ----------
  {
    id: "sales_lead_followup",
    name: "线索跟进流程",
    category: "sales",
    description: "从线索接收到转化的全流程跟进，含自动评分分级与销售分配。",
    expectedEffect: "线索响应时长 < 30 分钟，转化率提升 ≥ 25%。",
    nodes: [
      { id: "n1", type: "http", label: "线索接收" },
      { id: "n2", type: "ai_call", label: "线索评分分级" },
      { id: "n3", type: "condition", label: "分级路由" },
      { id: "n4", type: "notification", label: "分配销售" },
      { id: "n5", type: "ai_call", label: "跟进话术生成" },
      { id: "n6", type: "data_process", label: "转化记录" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      scoring_model: "lead_score_v2",
      high_threshold: "80",
      assign_strategy: "round_robin",
    },
    version: "1.3.1",
    createdAt: "2025-02-20T11:00:00Z",
    source: "builtin",
  },
  {
    id: "sales_customer_onboarding",
    name: "客户 Onboarding 流程",
    category: "sales",
    description: "签约客户从合同签订到完成上手的标准 Onboarding 流程。",
    expectedEffect: "Onboarding 周期缩短至 7 天内，客户首周活跃率 ≥ 90%。",
    nodes: [
      { id: "n1", type: "data_process", label: "合同签订确认" },
      { id: "n2", type: "notification", label: "发送欢迎邮件" },
      { id: "n3", type: "script", label: "账号与权限配置" },
      { id: "n4", type: "ai_call", label: "培训方案生成" },
      { id: "n5", type: "wait", label: "等待培训完成", config: { duration: "3d" } },
      { id: "n6", type: "notification", label: "首周回访" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      welcome_template: "onboarding_v3",
      training_duration_days: "3",
      checkin_day: "7",
    },
    version: "1.0.0",
    createdAt: "2025-03-25T09:45:00Z",
    source: "builtin",
  },
  {
    id: "sales_renewal_reminder",
    name: "续约提醒流程",
    category: "sales",
    description: "客户到期前的续约提醒流程，含风险评估与续约方案生成。",
    expectedEffect: "续约率提升 ≥ 15%，提前 30 天触达率 100%。",
    nodes: [
      { id: "n1", type: "data_process", label: "到期检测" },
      { id: "n2", type: "ai_call", label: "流失风险评估" },
      { id: "n3", type: "condition", label: "风险分级" },
      { id: "n4", type: "ai_call", label: "续约方案生成" },
      { id: "n5", type: "notification", label: "发送续约提醒" },
      { id: "n6", type: "notification", label: "销售跟进提醒" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      days_before_expire: "30",
      risk_threshold: "0.6",
      discount_max: "0.15",
    },
    version: "1.2.0",
    createdAt: "2025-04-10T16:20:00Z",
    source: "builtin",
  },
  {
    id: "sales_quote_approval",
    name: "报价审批流程",
    category: "sales",
    description: "销售报价申请的合规检查与多级审批流程。",
    expectedEffect: "报价审批平均时长 < 4 小时，合规拦截率 100%。",
    nodes: [
      { id: "n1", type: "data_process", label: "报价申请提交" },
      { id: "n2", type: "ai_call", label: "合规检查" },
      { id: "n3", type: "condition", label: "折扣是否超标" },
      { id: "n4", type: "approval", label: "主管审批" },
      { id: "n5", type: "approval", label: "财务审批" },
      { id: "n6", type: "notification", label: "发送报价单" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      discount_threshold: "0.1",
      approval_timeout_hours: "4",
      notify_channel: "email,lark",
    },
    version: "1.0.2",
    createdAt: "2025-05-02T13:10:00Z",
    source: "builtin",
  },

  // ---------- 运营类 ----------
  {
    id: "ops_daily_report",
    name: "日报生成流程",
    category: "operations",
    description: "每日业务数据采集、汇总、AI 生成日报并自动分发。",
    expectedEffect: "日报产出耗时 < 5 分钟，覆盖率 100%。",
    nodes: [
      { id: "n1", type: "http", label: "业务数据采集" },
      { id: "n2", type: "data_process", label: "数据汇总分析" },
      { id: "n3", type: "ai_call", label: "AI 生成日报" },
      { id: "n4", type: "approval", label: "审核" },
      { id: "n5", type: "notification", label: "分发" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
    ],
    defaultParams: {
      report_time: "09:00",
      data_sources: "crm,finance,marketing",
      recipients: "management",
    },
    version: "1.4.0",
    createdAt: "2025-01-15T07:00:00Z",
    source: "builtin",
  },
  {
    id: "ops_weekly_report",
    name: "周报生成流程",
    category: "operations",
    description: "每周业务指标对比、AI 生成周报并分发，含下周计划建议。",
    expectedEffect: "周报产出耗时 < 10 分钟，决策建议采纳率 ≥ 60%。",
    nodes: [
      { id: "n1", type: "data_process", label: "周数据汇总" },
      { id: "n2", type: "data_process", label: "指标环比对比" },
      { id: "n3", type: "ai_call", label: "AI 生成周报" },
      { id: "n4", type: "ai_call", label: "下周计划建议" },
      { id: "n5", type: "approval", label: "审核" },
      { id: "n6", type: "notification", label: "分发" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      report_day: "friday",
      report_time: "17:00",
      include_forecast: "true",
    },
    version: "1.1.0",
    createdAt: "2025-02-08T17:30:00Z",
    source: "builtin",
  },
  {
    id: "ops_data_report",
    name: "数据报表流程",
    category: "operations",
    description: "按需生成数据报表，含图表生成、报告组装与定时发送。",
    expectedEffect: "报表产出耗时 < 8 分钟，数据准确率 ≥ 99%。",
    nodes: [
      { id: "n1", type: "http", label: "数据查询" },
      { id: "n2", type: "data_process", label: "图表生成" },
      { id: "n3", type: "data_process", label: "报告组装" },
      { id: "n4", type: "approval", label: "审核" },
      { id: "n5", type: "notification", label: "发送" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
    ],
    defaultParams: {
      report_format: "pdf",
      chart_theme: "dark",
      send_to: "stakeholders",
    },
    version: "1.0.1",
    createdAt: "2025-03-03T10:00:00Z",
    source: "builtin",
  },
  {
    id: "ops_anomaly_handling",
    name: "异常处理流程",
    category: "operations",
    description: "业务异常自动检测、分级、通知与处置的应急流程。",
    expectedEffect: "异常发现到处置 < 15 分钟，误报率 < 10%。",
    nodes: [
      { id: "n1", type: "data_process", label: "异常检测" },
      { id: "n2", type: "ai_call", label: "分级判定" },
      { id: "n3", type: "condition", label: "严重度路由" },
      { id: "n4", type: "notification", label: "通知责任人" },
      { id: "n5", type: "script", label: "自动处置" },
      { id: "n6", type: "data_process", label: "复盘记录" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      detect_metrics: "revenue,traffic,conversion",
      critical_threshold: "0.5",
      notify_channel: "sms,lark",
    },
    version: "1.2.0",
    createdAt: "2025-04-18T09:20:00Z",
    source: "builtin",
  },

  // ---------- 人事类 ----------
  {
    id: "hr_onboarding",
    name: "入职流程",
    category: "hr",
    description: "新员工从 Offer 确认到完成入职引导的标准流程。",
    expectedEffect: "入职准备耗时 < 1 天，账号开通准确率 100%。",
    nodes: [
      { id: "n1", type: "approval", label: "Offer 确认" },
      { id: "n2", type: "data_process", label: "资料收集" },
      { id: "n3", type: "script", label: "账号开通" },
      { id: "n4", type: "ai_call", label: "培训计划生成" },
      { id: "n5", type: "notification", label: "入职引导通知" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
    ],
    defaultParams: {
      probation_months: "3",
      default_role: "staff",
      mentor_required: "true",
    },
    version: "1.0.0",
    createdAt: "2025-01-20T08:30:00Z",
    source: "builtin",
  },
  {
    id: "hr_offboarding",
    name: "离职流程",
    category: "hr",
    description: "员工离职申请、工作交接、资产回收与账号停用的标准流程。",
    expectedEffect: "离职流程耗时 < 5 天，资产回收率 100%。",
    nodes: [
      { id: "n1", type: "approval", label: "离职申请审批" },
      { id: "n2", type: "data_process", label: "工作交接" },
      { id: "n3", type: "script", label: "资产回收" },
      { id: "n4", type: "script", label: "账号停用" },
      { id: "n5", type: "data_process", label: "离职结算" },
      { id: "n6", type: "notification", label: "离职确认" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      notice_period_days: "30",
      handover_required: "true",
      final_pay_day: "15",
    },
    version: "1.0.0",
    createdAt: "2025-02-12T14:00:00Z",
    source: "builtin",
  },
  {
    id: "hr_training",
    name: "培训流程",
    category: "hr",
    description: "培训需求收集、计划制定、资源准备、执行与效果评估流程。",
    expectedEffect: "培训覆盖率 ≥ 95%，培训满意度 ≥ 4.5/5。",
    nodes: [
      { id: "n1", type: "data_process", label: "培训需求收集" },
      { id: "n2", type: "ai_call", label: "培训计划生成" },
      { id: "n3", type: "approval", label: "计划审批" },
      { id: "n4", type: "data_process", label: "资源准备" },
      { id: "n5", type: "notification", label: "培训执行通知" },
      { id: "n6", type: "ai_call", label: "效果评估" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      training_type: "skill",
      budget_per_person: "1000",
      eval_method: "questionnaire",
    },
    version: "1.1.0",
    createdAt: "2025-03-08T11:15:00Z",
    source: "builtin",
  },
  {
    id: "hr_performance_review",
    name: "绩效考核流程",
    category: "hr",
    description: "绩效考核周期启动、自评、上级评分、校准与结果反馈的标准流程。",
    expectedEffect: "绩效周期耗时缩短 30%，结果反馈及时率 100%。",
    nodes: [
      { id: "n1", type: "data_process", label: "周期启动" },
      { id: "n2", type: "notification", label: "员工自评通知" },
      { id: "n3", type: "wait", label: "等待自评完成", config: { duration: "7d" } },
      { id: "n4", type: "approval", label: "上级评分" },
      { id: "n5", type: "approval", label: "结果校准" },
      { id: "n6", type: "notification", label: "结果反馈" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      cycle: "quarterly",
      self_eval_days: "7",
      calibrate_meeting: "true",
    },
    version: "1.0.1",
    createdAt: "2025-04-22T15:45:00Z",
    source: "builtin",
  },

  // ---------- 财务类 ----------
  {
    id: "fin_expense_approval",
    name: "报销审批流程",
    category: "finance",
    description: "员工报销提交、合规检查、多级审批与付款的标准流程。",
    expectedEffect: "报销审批周期 < 3 天，合规拦截率 100%。",
    nodes: [
      { id: "n1", type: "data_process", label: "报销提交" },
      { id: "n2", type: "ai_call", label: "合规检查" },
      { id: "n3", type: "approval", label: "主管审批" },
      { id: "n4", type: "approval", label: "财务审批" },
      { id: "n5", type: "script", label: "付款执行" },
      { id: "n6", type: "notification", label: "付款通知" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      amount_threshold: "5000",
      approval_timeout_days: "3",
      payment_method: "bank_transfer",
    },
    version: "1.2.0",
    createdAt: "2025-01-28T09:00:00Z",
    source: "builtin",
  },
  {
    id: "fin_payment_approval",
    name: "付款审批流程",
    category: "finance",
    description: "对外付款申请、合同核对、多级审批与执行的流程。",
    expectedEffect: "付款准确率 100%，审批周期 < 2 天。",
    nodes: [
      { id: "n1", type: "data_process", label: "付款申请" },
      { id: "n2", type: "data_process", label: "合同核对" },
      { id: "n3", type: "approval", label: "主管审批" },
      { id: "n4", type: "approval", label: "财务审批" },
      { id: "n5", type: "script", label: "执行付款" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
    ],
    defaultParams: {
      contract_required: "true",
      approval_timeout_days: "2",
      payment_method: "bank_transfer",
    },
    version: "1.0.0",
    createdAt: "2025-02-25T10:30:00Z",
    source: "builtin",
  },
  {
    id: "fin_invoicing",
    name: "开票流程",
    category: "finance",
    description: "开票申请、客户信息核对、发票开具与发送归档的流程。",
    expectedEffect: "开票耗时 < 1 天，发票准确率 100%。",
    nodes: [
      { id: "n1", type: "data_process", label: "开票申请" },
      { id: "n2", type: "data_process", label: "客户信息核对" },
      { id: "n3", type: "approval", label: "开票审批" },
      { id: "n4", type: "script", label: "发票开具" },
      { id: "n5", type: "notification", label: "发送发票" },
      { id: "n6", type: "data_process", label: "归档" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
      { from: "n5", to: "n6" },
    ],
    defaultParams: {
      invoice_type: "vat_special",
      send_method: "email",
      archive_required: "true",
    },
    version: "1.0.0",
    createdAt: "2025-03-15T14:20:00Z",
    source: "builtin",
  },
  {
    id: "fin_reconciliation",
    name: "对账流程",
    category: "finance",
    description: "银行流水与系统账单自动对账、差异处理与报告生成的流程。",
    expectedEffect: "对账自动化率 ≥ 90%，差异处理时长 < 1 天。",
    nodes: [
      { id: "n1", type: "http", label: "数据拉取" },
      { id: "n2", type: "data_process", label: "账单匹配" },
      { id: "n3", type: "condition", label: "差异判定" },
      { id: "n4", type: "approval", label: "差异处理" },
      { id: "n5", type: "data_process", label: "生成对账报告" },
    ],
    edges: [
      { from: "n1", to: "n2" },
      { from: "n2", to: "n3" },
      { from: "n3", to: "n4" },
      { from: "n4", to: "n5" },
    ],
    defaultParams: {
      data_source: "bank,erp",
      match_strategy: "fuzzy",
      report_format: "xlsx",
    },
    version: "1.1.0",
    createdAt: "2025-04-05T16:00:00Z",
    source: "builtin",
  },
];

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 安全读取自定义模板 */
function loadCustomTemplates(): WorkflowTemplate[] {
  try {
    const raw = localStorage.getItem(CUSTOM_TEMPLATES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as WorkflowTemplate[]) : [];
  } catch {
    return [];
  }
}

/** 安全写入自定义模板 */
function saveCustomTemplates(templates: WorkflowTemplate[]): void {
  try {
    localStorage.setItem(CUSTOM_TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

/** 格式化时间为可读字符串 */
function formatDateTime(iso: string): string {
  try {
    return iso.slice(0, 16).replace("T", " ");
  } catch {
    return iso;
  }
}

// ============================================
// 主组件
// ============================================

export default function WorkflowTemplateLibrary() {
  // 类别 Tab
  const [activeCategory, setActiveCategory] = useState<"all" | TemplateCategory>("all");
  // 搜索关键词
  const [searchQuery, setSearchQuery] = useState("");
  // 自定义模板
  const [customTemplates, setCustomTemplates] = useState<WorkflowTemplate[]>([]);
  // 选中预览的模板
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  // 套用中的模板 id
  const [applyingId, setApplyingId] = useState<string | null>(null);
  // 套用结果（按模板 id 记录最近一次）
  const [applyResults, setApplyResults] = useState<
    Record<string, { success: boolean; message: string }>
  >({});
  // 是否显示"保存自定义模板"弹窗
  const [showSaveModal, setShowSaveModal] = useState(false);

  // 初次挂载：加载自定义模板
  useEffect(() => {
    setCustomTemplates(loadCustomTemplates());
  }, []);

  // 自定义模板变化时持久化
  useEffect(() => {
    saveCustomTemplates(customTemplates);
  }, [customTemplates]);

  // 合并预置 + 自定义模板（自定义排前）
  const allTemplates = useMemo<WorkflowTemplate[]>(
    () => [...customTemplates, ...BUILTIN_TEMPLATES],
    [customTemplates]
  );

  // 按类别 + 关键词筛选
  const filteredTemplates = useMemo(() => {
    const kw = searchQuery.trim().toLowerCase();
    return allTemplates.filter((t) => {
      if (activeCategory !== "all" && t.category !== activeCategory) return false;
      if (!kw) return true;
      return (
        t.name.toLowerCase().includes(kw) ||
        t.description.toLowerCase().includes(kw) ||
        t.expectedEffect.toLowerCase().includes(kw)
      );
    });
  }, [allTemplates, activeCategory, searchQuery]);

  // 各类别模板数量统计
  const categoryCounts = useMemo(() => {
    const counts: Record<"all" | TemplateCategory, number> = {
      all: allTemplates.length,
      marketing: 0,
      sales: 0,
      operations: 0,
      hr: 0,
      finance: 0,
    };
    for (const t of allTemplates) counts[t.category]++;
    return counts;
  }, [allTemplates]);

  // ============================================
  // 一键套用：调用 WorkflowRuntime 创建实例
  // ============================================
  const handleApply = useCallback(async (template: WorkflowTemplate) => {
    setApplyingId(template.id);
    // 清除该模板上一次结果
    setApplyResults((prev) => {
      const next = { ...prev };
      delete next[template.id];
      return next;
    });
    try {
      // 等待 RuntimeManager 初始化（最多 9 秒）
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setApplyResults((prev) => ({
          ...prev,
          [template.id]: {
            success: false,
            message: "RuntimeManager 尚未初始化",
          },
        }));
        return;
      }
      if (!RuntimeManager.hasRuntime(RuntimeName.Workflow)) {
        setApplyResults((prev) => ({
          ...prev,
          [template.id]: {
            success: false,
            message: "工作流运行时未注册",
          },
        }));
        return;
      }
      // 调用 WorkflowRuntime 创建实例（type: instantiate_template）
      const resp = await RuntimeManager.query(RuntimeName.Workflow, {
        type: "instantiate_template",
        templateId: template.id,
        template, // 同时携带完整模板，便于后端兜底构建
        params: template.defaultParams,
      });
      if (resp?.success) {
        setApplyResults((prev) => ({
          ...prev,
          [template.id]: {
            success: true,
            message: "已创建工作流实例",
          },
        }));
      } else {
        setApplyResults((prev) => ({
          ...prev,
          [template.id]: {
            success: false,
            message: resp?.error || "套用失败",
          },
        }));
      }
    } catch (err) {
      setApplyResults((prev) => ({
        ...prev,
        [template.id]: {
          success: false,
          message: (err as Error).message || "套用异常",
        },
      }));
    } finally {
      setApplyingId(null);
    }
  }, []);

  // ============================================
  // 删除自定义模板
  // ============================================
  const handleDeleteCustom = useCallback(
    (templateId: string) => {
      setCustomTemplates((prev) => prev.filter((t) => t.id !== templateId));
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
      }
    },
    [selectedTemplate]
  );

  // ============================================
  // 保存自定义模板（由 SaveCustomTemplateModal 回调）
  // ============================================
  const handleSaveCustom = useCallback(
    (draft: Omit<WorkflowTemplate, "id" | "version" | "createdAt" | "source">) => {
      const now = new Date().toISOString();
      // 节点已带 id，边在 Modal 内按线性链生成
      const newTemplate: WorkflowTemplate = {
        ...draft,
        id: genId("custom"),
        version: "1.0.0",
        createdAt: now,
        source: "custom",
      };
      setCustomTemplates((prev) => [newTemplate, ...prev]);
      setShowSaveModal(false);
    },
    []
  );

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-sky-500/15 border border-sky-500/30">
            <LayoutTemplate size={13} className="text-sky-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              预置工作流模板库
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Template Library · 预置 {BUILTIN_TEMPLATES.length} · 自定义 {customTemplates.length}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowSaveModal(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
          >
            <Plus size={11} className="text-emerald-400" />
            <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">
              保存模板
            </span>
          </button>
          <button
            onClick={() => setCustomTemplates(loadCustomTemplates())}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-sky-500/15 border border-sky-500/30 hover:bg-sky-500/25 transition-colors"
          >
            <RefreshCw size={11} className="text-sky-400" />
            <span className="text-[9px] font-mono text-sky-300 uppercase tracking-wider">
              刷新
            </span>
          </button>
        </div>
      </div>

      {/* 类别 Tab + 搜索框 */}
      <div className="p-4 border-b border-white/[0.04] space-y-2">
        <div className="flex items-center gap-1 flex-wrap">
          <Filter size={10} className="text-gray-500 flex-shrink-0" />
          {CATEGORY_FILTERS.map((f) => {
            const isActive = activeCategory === f.id;
            const count = categoryCounts[f.id];
            return (
              <button
                key={f.id}
                onClick={() => setActiveCategory(f.id)}
                className={`px-2 py-1 rounded-md text-[10px] font-mono transition-colors flex items-center gap-1.5 ${
                  isActive
                    ? "bg-sky-500/15 text-sky-300 border border-sky-500/30"
                    : "text-gray-500 hover:text-gray-300 border border-transparent"
                }`}
              >
                {f.label}
                <span
                  className={`px-1 rounded text-[8px] ${
                    isActive ? "bg-sky-500/20 text-sky-300" : "bg-white/[0.05] text-gray-600"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative">
          <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索模板名称 / 说明 / 预期效果"
            className="w-full pl-7 pr-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-sky-500/40"
          />
        </div>
      </div>

      {/* 模板卡片网格 */}
      <div className="p-4">
        {filteredTemplates.length === 0 ? (
          <div className="py-10 text-center">
            <LayoutTemplate size={22} className="mx-auto text-gray-600 mb-2" />
            <p className="text-[11px] font-mono text-gray-500">未找到匹配的模板</p>
            <p className="text-[9px] font-mono text-gray-700 mt-1">
              调整搜索关键词或类别筛选
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {filteredTemplates.map((tpl) => (
              <TemplateCard
                key={tpl.id}
                template={tpl}
                applying={applyingId === tpl.id}
                applyResult={applyResults[tpl.id]}
                onPreview={() => setSelectedTemplate(tpl)}
                onApply={() => void handleApply(tpl)}
                onDelete={
                  tpl.source === "custom" ? () => handleDeleteCustom(tpl.id) : undefined
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* 模板详情 Modal */}
      <AnimatePresence>
        {selectedTemplate && (
          <TemplateDetailModal
            template={selectedTemplate}
            applying={applyingId === selectedTemplate.id}
            applyResult={applyResults[selectedTemplate.id]}
            onClose={() => setSelectedTemplate(null)}
            onApply={() => void handleApply(selectedTemplate)}
            onDelete={
              selectedTemplate.source === "custom"
                ? () => handleDeleteCustom(selectedTemplate.id)
                : undefined
            }
          />
        )}
      </AnimatePresence>

      {/* 保存自定义模板 Modal */}
      <AnimatePresence>
        {showSaveModal && (
          <SaveCustomTemplateModal
            onClose={() => setShowSaveModal(false)}
            onSave={handleSaveCustom}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：模板卡片
// ============================================
interface TemplateCardProps {
  template: WorkflowTemplate;
  applying: boolean;
  applyResult?: { success: boolean; message: string };
  onPreview: () => void;
  onApply: () => void;
  onDelete?: () => void;
}

function TemplateCard({
  template,
  applying,
  applyResult,
  onPreview,
  onApply,
  onDelete,
}: TemplateCardProps) {
  const catMeta = CATEGORY_META[template.category];
  const CatIcon = catMeta.icon;
  const nodeCount = template.nodes.length;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
      className="group relative rounded-lg border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.1] transition-colors p-3 flex flex-col gap-2"
      style={{ borderLeft: `2px solid ${catMeta.color}` }}
    >
      {/* 卡片头部：图标 + 名称 + 来源标记 */}
      <div className="flex items-start gap-2">
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 border"
          style={{
            background: `${catMeta.color}15`,
            borderColor: `${catMeta.color}30`,
          }}
        >
          <CatIcon size={13} style={{ color: catMeta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[12px] font-semibold text-gray-100 truncate">
              {template.name}
            </span>
            {template.source === "custom" && (
              <span className="px-1 rounded text-[8px] font-mono bg-emerald-500/15 text-emerald-300 flex-shrink-0">
                自定义
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className="px-1 rounded text-[8px] font-mono"
              style={{ background: `${catMeta.color}20`, color: catMeta.color }}
            >
              {catMeta.label}
            </span>
            <span className="flex items-center gap-0.5 text-[8px] font-mono text-gray-500">
              <Layers size={8} />
              {nodeCount} 节点
            </span>
            <span className="flex items-center gap-0.5 text-[8px] font-mono text-gray-600">
              v{template.version}
            </span>
          </div>
        </div>
        {onDelete && (
          <button
            onClick={onDelete}
            title="删除自定义模板"
            className="p-1 rounded text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0"
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>

      {/* 说明 */}
      <p className="text-[10px] font-mono text-gray-400 leading-relaxed line-clamp-2">
        {template.description}
      </p>

      {/* 套用结果提示 */}
      {applyResult && (
        <div
          className={`flex items-center gap-1.5 px-2 py-1 rounded text-[9px] font-mono ${
            applyResult.success
              ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
              : "bg-red-500/10 text-red-300 border border-red-500/20"
          }`}
        >
          {applyResult.success ? (
            <CheckCircle2 size={10} className="flex-shrink-0" />
          ) : (
            <AlertTriangle size={10} className="flex-shrink-0" />
          )}
          <span className="truncate">{applyResult.message}</span>
        </div>
      )}

      {/* 卡片底部：预览 + 套用按钮 */}
      <div className="flex items-center gap-1.5 mt-auto pt-1">
        <button
          onClick={onPreview}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-white/[0.04] border border-white/[0.06] text-gray-300 hover:bg-white/[0.08] transition-colors"
        >
          <FileText size={10} />
          预览
        </button>
        <button
          onClick={onApply}
          disabled={applying}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-sky-500/15 border border-sky-500/30 text-sky-300 hover:bg-sky-500/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {applying ? (
            <Loader2 size={10} className="animate-spin" />
          ) : (
            <Sparkles size={10} />
          )}
          {applying ? "套用中..." : "一键套用"}
        </button>
      </div>
    </motion.div>
  );
}

// ============================================
// 子组件：模板详情 Modal
// ============================================
interface TemplateDetailModalProps {
  template: WorkflowTemplate;
  applying: boolean;
  applyResult?: { success: boolean; message: string };
  onClose: () => void;
  onApply: () => void;
  onDelete?: () => void;
}

function TemplateDetailModal({
  template,
  applying,
  applyResult,
  onClose,
  onApply,
  onDelete,
}: TemplateDetailModalProps) {
  const catMeta = CATEGORY_META[template.category];
  const CatIcon = catMeta.icon;

  // 构建 DAG 节点查找表（按 id 索引）
  const nodeMap = useMemo(() => {
    const m = new Map<string, TemplateNode>();
    for (const n of template.nodes) m.set(n.id, n);
    return m;
  }, [template.nodes]);

  // 计算每个节点的下游节点（用于在节点列表下方展示连接关系）
  const downstreamMap = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const e of template.edges) {
      if (!m.has(e.from)) m.set(e.from, []);
      m.get(e.from)!.push(e.to);
    }
    return m;
  }, [template.edges]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#131826] to-[#0a0e1a] backdrop-blur-md flex flex-col"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 border"
              style={{
                background: `${catMeta.color}15`,
                borderColor: `${catMeta.color}30`,
              }}
            >
              <CatIcon size={14} style={{ color: catMeta.color }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[14px] font-semibold text-white truncate">
                  {template.name}
                </span>
                {template.source === "custom" && (
                  <span className="px-1 rounded text-[8px] font-mono bg-emerald-500/15 text-emerald-300">
                    自定义
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5 text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                <span style={{ color: catMeta.color }}>{catMeta.label}</span>
                <span>·</span>
                <span className="flex items-center gap-0.5">
                  <Layers size={8} />
                  {template.nodes.length} 节点
                </span>
                <span>·</span>
                <span className="flex items-center gap-0.5">
                  <GitBranch size={8} />v{template.version}
                </span>
                <span>·</span>
                <span className="flex items-center gap-0.5">
                  <Calendar size={8} />
                  {formatDateTime(template.createdAt)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {onDelete && (
              <button
                onClick={onDelete}
                title="删除自定义模板"
                className="p-1 rounded-md text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 size={13} />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 rounded-md hover:bg-white/[0.05] transition-colors"
            >
              <X size={14} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* 说明文档 */}
          <section>
            <SectionTitle icon={FileText} label="说明文档" color="#38BDF8" />
            <p className="mt-2 text-[11px] font-mono text-gray-300 leading-relaxed px-3 py-2 rounded-md border border-white/[0.04] bg-white/[0.02]">
              {template.description}
            </p>
          </section>

          {/* 预期效果 */}
          <section>
            <SectionTitle icon={TrendingUp} label="预期效果" color="#34D399" />
            <p className="mt-2 text-[11px] font-mono text-emerald-300/90 leading-relaxed px-3 py-2 rounded-md border border-emerald-500/15 bg-emerald-500/[0.04]">
              {template.expectedEffect}
            </p>
          </section>

          {/* DAG 节点列表 */}
          <section>
            <SectionTitle icon={GitBranch} label={`DAG 节点列表（${template.nodes.length}）`} color="#A78BFA" />
            <div className="mt-2 space-y-1.5">
              {template.nodes.map((node, idx) => {
                const meta = NODE_TYPE_META[node.type];
                const NodeIcon = meta.icon;
                const downstream = downstreamMap.get(node.id) || [];
                return (
                  <div key={node.id}>
                    <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-white/[0.04] bg-white/[0.02]">
                      <span className="text-[9px] font-mono text-gray-600 w-4 flex-shrink-0">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <NodeIcon size={12} style={{ color: meta.color }} className="flex-shrink-0" />
                      <span className="text-[11px] text-gray-200 flex-1 truncate">
                        {node.label}
                      </span>
                      <span
                        className="px-1 rounded text-[8px] font-mono"
                        style={{ background: `${meta.color}20`, color: meta.color }}
                      >
                        {meta.label}
                      </span>
                      <span className="text-[8px] font-mono text-gray-600">{node.id}</span>
                    </div>
                    {/* 连接线 + 下游标识 */}
                    {downstream.length > 0 && (
                      <div className="flex items-center gap-1 pl-6 py-0.5">
                        <span className="text-[8px] font-mono text-gray-700">↓</span>
                        <span className="text-[8px] font-mono text-gray-600">
                          → {downstream.map((d) => nodeMap.get(d)?.label || d).join(" / ")}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* 默认参数 */}
          <section>
            <SectionTitle
              icon={Code}
              label={`默认参数（${Object.keys(template.defaultParams).length}）`}
              color="#FBBF24"
            />
            {Object.keys(template.defaultParams).length === 0 ? (
              <p className="mt-2 text-[10px] font-mono text-gray-600 px-2 py-1">无默认参数</p>
            ) : (
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                {Object.entries(template.defaultParams).map(([k, v]) => (
                  <div
                    key={k}
                    className="px-2 py-1.5 rounded-md border border-white/[0.04] bg-white/[0.02]"
                  >
                    <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">
                      {k}
                    </div>
                    <div className="text-[11px] font-mono text-amber-300 truncate">{v}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* 套用结果提示 */}
          {applyResult && (
            <div
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-[10px] font-mono ${
                applyResult.success
                  ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
                  : "bg-red-500/10 text-red-300 border border-red-500/20"
              }`}
            >
              {applyResult.success ? (
                <CheckCircle2 size={12} className="flex-shrink-0" />
              ) : (
                <AlertTriangle size={12} className="flex-shrink-0" />
              )}
              <span>{applyResult.message}</span>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-white/[0.06] flex items-center justify-between gap-2">
          <div className="text-[9px] font-mono text-gray-600">
            模板 ID：<span className="text-gray-400">{template.id}</span>
          </div>
          <button
            onClick={onApply}
            disabled={applying}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-sky-500/15 border border-sky-500/30 text-sky-300 hover:bg-sky-500/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {applying ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Sparkles size={12} />
            )}
            {applying ? "套用中..." : "一键套用"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 子组件：保存自定义模板 Modal
// 用户可将自己的工作流保存为模板（节点按线性链自动连接）
// ============================================
interface SaveCustomTemplateModalProps {
  onClose: () => void;
  onSave: (
    draft: Omit<WorkflowTemplate, "id" | "version" | "createdAt" | "source">
  ) => void;
}

function SaveCustomTemplateModal({ onClose, onSave }: SaveCustomTemplateModalProps) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<TemplateCategory>("operations");
  const [description, setDescription] = useState("");
  const [expectedEffect, setExpectedEffect] = useState("");
  // 节点编辑列表（type + label）
  const [nodes, setNodes] = useState<{ type: TemplateNodeType; label: string }[]>([
    { type: "ai_call", label: "" },
  ]);
  // 默认参数（key=value 文本，每行一项）
  const [paramsText, setParamsText] = useState("");

  /** 添加节点 */
  const addNode = () => {
    setNodes((prev) => [...prev, { type: "ai_call", label: "" }]);
  };

  /** 删除节点 */
  const removeNode = (idx: number) => {
    setNodes((prev) => prev.filter((_, i) => i !== idx));
  };

  /** 更新节点 */
  const updateNode = (idx: number, patch: Partial<{ type: TemplateNodeType; label: string }>) => {
    setNodes((prev) =>
      prev.map((n, i) => (i === idx ? { ...n, ...patch } : n))
    );
  };

  /** 提交保存 */
  const handleSubmit = () => {
    // 过滤掉无 label 的节点
    const validNodes = nodes.filter((n) => n.label.trim());
    if (!name.trim() || validNodes.length < 2) return;

    // 构造 DAG 节点（带 id）
    const tplNodes: TemplateNode[] = validNodes.map((n, i) => ({
      id: `n${i + 1}`,
      type: n.type,
      label: n.label.trim(),
    }));

    // 边按线性链自动连接（n1→n2→n3...）
    const edges: { from: string; to: string }[] = [];
    for (let i = 0; i < tplNodes.length - 1; i++) {
      edges.push({ from: tplNodes[i].id, to: tplNodes[i + 1].id });
    }

    // 解析默认参数（每行 key=value）
    const defaultParams: Record<string, string> = {};
    for (const line of paramsText.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const eqIdx = trimmed.indexOf("=");
      if (eqIdx > 0) {
        const k = trimmed.slice(0, eqIdx).trim();
        const v = trimmed.slice(eqIdx + 1).trim();
        if (k) defaultParams[k] = v;
      }
    }

    onSave({
      name: name.trim(),
      category,
      description: description.trim() || "用户自定义工作流模板",
      expectedEffect: expectedEffect.trim() || "按用户配置执行",
      nodes: tplNodes,
      edges,
      defaultParams,
    });
  };

  const canSubmit = name.trim().length > 0 && nodes.filter((n) => n.label.trim()).length >= 2;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl max-h-[85vh] overflow-hidden rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#131826] to-[#0a0e1a] backdrop-blur-md flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Save size={14} className="text-emerald-400" />
            <span className="text-[13px] font-semibold text-white">保存为工作流模板</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-white/[0.05] transition-colors"
          >
            <X size={14} className="text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* 名称 + 类别 */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                模板名称 *
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="如：客户回访流程"
                className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
              />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                类别
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as TemplateCategory)}
                className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
              >
                {Object.entries(CATEGORY_META).map(([k, v]) => (
                  <option key={k} value={k} className="bg-[#131826]">
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 说明 */}
          <div>
            <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              说明文档
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="模板用途与执行逻辑说明"
              rows={2}
              className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40 resize-none"
            />
          </div>

          {/* 预期效果 */}
          <div>
            <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              预期效果
            </label>
            <input
              value={expectedEffect}
              onChange={(e) => setExpectedEffect(e.target.value)}
              placeholder="如：响应时长 < 1 小时"
              className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
            />
          </div>

          {/* DAG 节点编辑 */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                DAG 节点（至少 2 个，按顺序线性连接）
              </label>
              <button
                onClick={addNode}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
              >
                <Plus size={9} />
                添加节点
              </button>
            </div>
            <div className="mt-1.5 space-y-1.5">
              {nodes.map((node, idx) => {
                const meta = NODE_TYPE_META[node.type];
                const NodeIcon = meta.icon;
                return (
                  <div key={idx} className="flex items-center gap-1.5">
                    <span className="text-[9px] font-mono text-gray-600 w-4 flex-shrink-0">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <NodeIcon size={11} style={{ color: meta.color }} className="flex-shrink-0" />
                    <select
                      value={node.type}
                      onChange={(e) =>
                        updateNode(idx, { type: e.target.value as TemplateNodeType })
                      }
                      className="px-1.5 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
                    >
                      {NODE_TYPE_OPTIONS.map((t) => (
                        <option key={t} value={t} className="bg-[#131826]">
                          {NODE_TYPE_META[t].label}
                        </option>
                      ))}
                    </select>
                    <input
                      value={node.label}
                      onChange={(e) => updateNode(idx, { label: e.target.value })}
                      placeholder="节点名称"
                      className="flex-1 px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40"
                    />
                    {nodes.length > 1 && (
                      <button
                        onClick={() => removeNode(idx)}
                        className="p-1 rounded text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0"
                      >
                        <X size={11} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 默认参数 */}
          <div>
            <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              默认参数（每行 key=value）
            </label>
            <textarea
              value={paramsText}
              onChange={(e) => setParamsText(e.target.value)}
              placeholder={"channel=email\ntimeout=3600"}
              rows={3}
              className="mt-1 w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-emerald-500/40 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/[0.06] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-white/[0.04] border border-white/[0.06] text-gray-300 hover:bg-white/[0.08] transition-colors"
          >
            <X size={12} />
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save size={12} />
            保存模板
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 子组件：区块标题
// ============================================
function SectionTitle({
  icon: Icon,
  label,
  color,
}: {
  icon: typeof FileText;
  label: string;
  color: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon size={12} style={{ color }} />
      <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color }}>
        {label}
      </span>
    </div>
  );
}
